import { Component, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faEdit, faPlus, faTrash } from '@fortawesome/free-solid-svg-icons';
import { Dialog, DialogModule } from '@angular/cdk/dialog';
import { Workflow, WorkflowAction, WorkflowTrigger } from '@clientwire/api';
import { WorkflowDialogComponent } from './workflow-dialog/workflow-dialog.component';
import { WorkflowsService } from './workflows.service';
import { ButtonComponent } from '@akostm/orbit-ui';

@Component({
  selector: 'lib-settings-workflows',
  standalone: true,
  imports: [CommonModule, TranslateModule, FontAwesomeModule, DialogModule, ButtonComponent],
  templateUrl: './workflows.component.html',
  styleUrls: ['./workflows.component.scss'],
})
export class WorkflowsComponent implements OnInit {
  readonly #workflowService = inject(WorkflowsService);
  readonly #translateService = inject(TranslateService);
  readonly #dialog = inject(Dialog);

  // Workflows data
  readonly workflows = this.#workflowService.workflows;
  readonly loading = this.#workflowService.loading;
  readonly error = this.#workflowService.error;

  // Font Awesome icons
  faEdit = faEdit;
  faTrash = faTrash;
  faPlus = faPlus;

  ngOnInit(): void {
    this.#workflowService.loadWorkflows();
  }

  addWorkflow(): void {
    const dialogRef = this.#dialog.open(WorkflowDialogComponent, {
      data: {
        title: this.#translateService.instant('SETTINGS.WORKFLOWS.ADD_WORKFLOW'),
        confirmLabel: this.#translateService.instant('SETTINGS.WORKFLOWS.CREATE'),
      },
      width: '44rem',
    });

    dialogRef.closed.subscribe((result) => {
      if (result) {
        this.#workflowService.createWorkflow(result);
      }
    });
  }

  editWorkflow(workflow: Workflow): void {
    const dialogRef = this.#dialog.open(WorkflowDialogComponent, {
      data: {
        title: this.#translateService.instant('SETTINGS.WORKFLOWS.EDIT_WORKFLOW'),
        confirmLabel: this.#translateService.instant('SETTINGS.WORKFLOWS.SAVE'),
        workflow: workflow,
      },
      width: '44rem',
    });

    dialogRef.closed.subscribe((result) => {
      if (result) {
        this.#workflowService.updateWorkflow(workflow.id!, result, workflow.enabled);
      }
    });
  }

  deleteWorkflow(workflow: Workflow): void {
    const dialogRef = this.#dialog.open(WorkflowDialogComponent, {
      data: {
        title: this.#translateService.instant('SETTINGS.WORKFLOWS.CONFIRM_DELETE_TITLE'),
        confirmLabel: this.#translateService.instant('SETTINGS.WORKFLOWS.DELETE'),
        message: this.#translateService.instant('SETTINGS.WORKFLOWS.CONFIRM_DELETE', {
          id: workflow.id,
        }),
        isConfirmation: true,
      },
      width: '32rem',
    });

    dialogRef.closed.subscribe((result) => {
      if (result) {
        this.#workflowService.deleteWorkflow(workflow.id!);
      }
    });
  }

  getTriggerName(trigger: WorkflowTrigger): string {
    return trigger.kind || 'Unknown Trigger';
  }

  getActionName(action: WorkflowAction | null): string {
    return action?.kind || '';
  }

  toggleWorkflowEnabled(workflow: Workflow, event: Event): void {
    // Prevent the click from propagating to parent elements
    event.stopPropagation();

    // Get the new status from the checkbox input
    const enabled = (event.target as HTMLInputElement).checked;

    // Call the service to update the status
    this.#workflowService.toggleWorkflowEnabled(workflow.id!, enabled);
  }
}
